
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAppSelector } from './store/hooks';
import { RootState } from './store/store';
import apiClient from "./util/apiClient";

interface ThemeContextType {
  theme: string;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => { },
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const userInfo = useAppSelector((state: RootState) => state.userState);
  const userId = userInfo?.userInfo?._id;

  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const fetchThemeFromBackend = async () => {
      const currentPath = window.location.pathname;

      if (userId && currentPath === '/manage-company') {
        try {
          const res = await apiClient(`users/getTheme/${userId}`, "GET");
          if (res?.result?.theme) {
            const userTheme = res.result.theme;
            setTheme(userTheme);
            document.documentElement.setAttribute('data-theme', userTheme);
            localStorage.setItem('theme', userTheme);
          }
        } catch (err) {
          console.error("Failed to fetch theme from backend:", err);
        }
      }
    };

    fetchThemeFromBackend();
  }, [userId]);

  useEffect(() => {
    if (theme) {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);

    try {
      await apiClient(`users/toggleTheme/${userId}`, "POST", { theme: newTheme });
    } catch (error) {
      console.error("Failed to update theme on server:", error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);


